<template>
  <div class="address-form">
    <table class="first-dark">
      <tbody>
        <tr>
          <td>{{ $t("message.order.name") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="name"
              placeholder="name"
              v-model="localAddress.name"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.name }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.company") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="company"
              placeholder="company"
              v-model="localAddress.company"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.company }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.street_1") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="street1"
              placeholder="street1"
              v-model="localAddress.street1"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.street1 }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.street_2") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="street2"
              placeholder="street2"
              v-model="localAddress.street2"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.street2 }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.street_3") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="street3"
              placeholder="street3"
              v-model="localAddress.street3"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.street3 }}
            </template>
          </td>
        </tr>
        <tr>
          <td>Provincia</td>
          <td>
            <select
              class="regular full bordered"
              v-if="canEdit"
              name="provincia"
              v-model="localAddress.codProvincia"
              @change="onProvinciaChange"
              :disabled="disabled"
            >
              <option value="">Seleccione una provincia</option>
              <option
                v-for="provincia in provincias"
                :key="provincia.codProvincia"
                :value="provincia.codProvincia"
              >
                {{ provincia.nombreProvincia }}
              </option>
            </select>
            <template v-else>
              {{ localAddress.provincia }}
            </template>
          </td>
        </tr>
        <tr>
          <td>Cantón</td>
          <td>
            <select
              class="regular full bordered"
              v-if="canEdit"
              name="canton"
              v-model="localAddress.codCanton"
              @change="onCantonChange"
              :disabled="disabled || !localAddress.codProvincia"
            >
              <option value="">Seleccione un cantón</option>
              <option
                v-for="canton in cantones"
                :key="canton.codCanton"
                :value="canton.codCanton"
              >
                {{ canton.nombreCanton }}
              </option>
            </select>
            <template v-else>
              {{ localAddress.canton }}
            </template>
          </td>
        </tr>
        <tr>
          <td>Distrito</td>
          <td>
            <select
              class="regular full bordered"
              v-if="canEdit"
              name="distrito"
              v-model="localAddress.codDistrito"
              @change="onDistritoChange"
              :disabled="disabled || !localAddress.codCanton"
            >
              <option value="">Seleccione un distrito</option>
              <option
                v-for="distrito in distritos"
                :key="distrito.codDistrito"
                :value="distrito.codDistrito"
              >
                {{ distrito.nombreDistrito }}
              </option>
            </select>
            <template v-else>
              {{ localAddress.distrito }}
            </template>
          </td>
        </tr>
        <tr>
          <td>Barrio</td>
          <td>
            <select
              class="regular full bordered"
              v-if="canEdit"
              name="barrio"
              v-model="localAddress.codBarrio"
              @change="onBarrioChange"
              :disabled="disabled || !localAddress.codDistrito"
            >
              <option value="">Seleccione un barrio (opcional)</option>
              <option
                v-for="barrio in barrios"
                :key="barrio.codBarrio"
                :value="barrio.codBarrio"
              >
                {{ barrio.nombreBarrio }}
              </option>
            </select>
            <template v-else>
              {{ localAddress.barrio }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.postal_code") }}</td>
          <td>
            <input
              class="regular full bordered"
              v-if="canEdit"
              type="text"
              name="postalCode"
              placeholder="postal code"
              v-model="localAddress.postalCode"
              :disabled="disabled"
            />
            <template v-else>
              {{ localAddress.postalCode }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.country") }}</td>
          <td>
            <input
              class="regular full bordered"
              type="text"
              name="country"
              v-model="localAddress.country"
              disabled
            />
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.residential") }}</td>
          <td>
            <select
              class="regular full bordered"
              v-if="canEdit"
              name="residential"
              v-model="localAddress.residential"
              :disabled="disabled"
            >
              <option value="1">{{ $t("common.yes") }}</option>
              <option value="0">{{ $t("common.no") }}</option>
            </select>
            <template v-else>
              {{
                localAddress.residential === "1"
                  ? $t("common.yes")
                  : $t("common.no")
              }}
            </template>
          </td>
        </tr>
        <tr>
          <td>{{ $t("message.order.phone") }}</td>
          <td>
            <div v-if="canEdit" class="flex flex-row gap-2">
              <input
                class="regular bordered w-24"
                type="text"
                name="phone-area-code"
                :placeholder="$t('create_infoauto_customer_modal.area_code')"
                :maxlength="areaCodeMaxLength"
                v-model.trim="localAddress.phoneAreaCode"
                :disabled="phoneDisabled"
              />
              <input
                class="regular full bordered"
                type="text"
                name="phone"
                placeholder="phone"
                v-model="localAddress.phone"
                :disabled="phoneDisabled"
              />
            </div>
            <template v-else>
              {{ formatPhone(localAddress.phoneAreaCode, localAddress.phone) }}
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { AREA_CODE_MAX_LENGTH } from "@/constants/infoautoPersona";
import { formatPhone } from "@/utils/phone";
import {
  getProvincias,
  getCantones,
  getDistritos,
  getBarrios
} from "@/api/store/CostaRicaLocations.js";

export default {
  name: "CostaRicaAddressForm",
  props: {
    address: {
      type: Object,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    phoneDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localAddress: {
        ...this.address,
        country: "COSTA RICA"
      },
      provincias: [],
      cantones: [],
      distritos: [],
      barrios: [],
      areaCodeMaxLength: AREA_CODE_MAX_LENGTH
    };
  },
  watch: {
    address: {
      handler(val) {
        this.localAddress = {
          ...val,
          country: "COSTA RICA"
        };
        this.loadLocationData();
      },
      deep: true
    },
    localAddress: {
      handler(val) {
        this.$emit("update:address", val);
      },
      deep: true
    }
  },
  mounted() {
    this.loadProvincias();
    if (this.localAddress.codProvincia) {
      this.loadLocationData();
    }
  },
  methods: {
    formatPhone,
    async loadProvincias() {
      try {
        this.provincias = await getProvincias(this.localAddress.country);
      } catch (error) {
        console.error("Error loading provincias:", error);
      }
    },
    async loadCantones(provinciaCode) {
      try {
        this.cantones = await getCantones(
          provinciaCode,
          this.localAddress.codPais,
          this.localAddress.country
        );
      } catch (error) {
        console.error("Error loading cantones:", error);
      }
    },
    async loadDistritos(provinciaCode, cantonCode) {
      try {
        this.distritos = await getDistritos(
          provinciaCode,
          cantonCode,
          this.localAddress.codPais,
          this.localAddress.country
        );
      } catch (error) {
        console.error("Error loading distritos:", error);
      }
    },
    async loadBarrios(provinciaCode, cantonCode, distritoCode) {
      try {
        this.barrios = await getBarrios(
          provinciaCode,
          cantonCode,
          distritoCode,
          this.localAddress.codPais,
          this.localAddress.country
        );
      } catch (error) {
        console.error("Error loading barrios:", error);
      }
    },
    async loadLocationData() {
      if (this.localAddress.codProvincia) {
        await this.loadCantones(this.localAddress.codProvincia);
      }
      if (this.localAddress.codCanton) {
        await this.loadDistritos(
          this.localAddress.codProvincia,
          this.localAddress.codCanton
        );
      }
      if (this.localAddress.codDistrito) {
        await this.loadBarrios(
          this.localAddress.codProvincia,
          this.localAddress.codCanton,
          this.localAddress.codDistrito
        );
      }
    },
    async onProvinciaChange() {
      const selected = this.provincias.find(
        p => String(p.codProvincia) === String(this.localAddress.codProvincia)
      );
      this.localAddress.provincia = selected?.nombreProvincia || "";
      this.localAddress.codCanton = "";
      this.localAddress.canton = "";
      this.localAddress.codDistrito = "";
      this.localAddress.distrito = "";
      this.localAddress.codBarrio = "";
      this.localAddress.barrio = "";
      this.cantones = [];
      this.distritos = [];
      this.barrios = [];
      if (this.localAddress.codProvincia) {
        await this.loadCantones(this.localAddress.codProvincia);
      }
    },
    async onCantonChange() {
      const selected = this.cantones.find(
        c => String(c.codCanton) === String(this.localAddress.codCanton)
      );
      this.localAddress.canton = selected?.nombreCanton || "";
      this.localAddress.codDistrito = "";
      this.localAddress.distrito = "";
      this.localAddress.codBarrio = "";
      this.localAddress.barrio = "";
      this.distritos = [];
      this.barrios = [];
      if (this.localAddress.codCanton) {
        await this.loadDistritos(
          this.localAddress.codProvincia,
          this.localAddress.codCanton
        );
      }
    },
    async onDistritoChange() {
      const selected = this.distritos.find(
        d => String(d.codDistrito) === String(this.localAddress.codDistrito)
      );
      this.localAddress.distrito = selected?.nombreDistrito || "";
      this.localAddress.codBarrio = "";
      this.localAddress.barrio = "";
      this.barrios = [];
      if (this.localAddress.codDistrito) {
        await this.loadBarrios(
          this.localAddress.codProvincia,
          this.localAddress.codCanton,
          this.localAddress.codDistrito
        );
      }
    },
    onBarrioChange() {
      const selected = this.barrios.find(
        b => String(b.codBarrio) === String(this.localAddress.codBarrio)
      );
      this.localAddress.barrio = selected?.nombreBarrio || "";
    }
  }
};
</script>

<style scoped>
.first-dark td:first-child {
  background-color: #222222;
  color: white;
  font-weight: 600;
  padding-left: 8px;
  width: 120px;
}

.first-dark tr:nth-child(even) {
  background-color: #f9fafb;
}
</style>
